/**
 * 应用版本管理 API
 */
import type {
  AppVersion,
  UpdateInfo,
  VersionCheckRequest,
  VersionListQuery,
  VersionListResponse,
  GrayDeviceWhitelist,
} from '@xiaozhi/shared-types';

const API_BASE = '/api/app-version';

function getAuthHeaders(isJson = true): HeadersInit {
  const authStore = localStorage.getItem("auth-storage");
  if (authStore) {
    const { state } = JSON.parse(authStore);
    const token = state?.accessToken;
    if (token) {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      if (isJson) {
        headers["Content-Type"] = "application/json";
      }
      return headers;
    }
  }
  return {};
}

/**
 * 检查版本更新（公开接口）
 */
export async function checkAppUpdate(request: VersionCheckRequest): Promise<UpdateInfo> {
  const params = new URLSearchParams({
    platform: 'android',
    current_version: request.version,
    current_version_code: request.versionCode.toString(),
    device_id: request.deviceId,
    ...(request.channel && { channel: request.channel }),
    ...(request.osVersion && { os_version: request.osVersion }),
  });

  const response = await fetch(`${API_BASE}/check?${params}`);
  const data = await response.json();

  // 服务端使用 {success: true, data: {...}} 格式
  if (!data.success || !data.data) {
    throw new Error(data.message || '检查更新失败');
  }

  return data.data;
}

/**
 * 获取版本列表
 */
export async function getVersionList(query: VersionListQuery): Promise<VersionListResponse> {
  const params = new URLSearchParams();
  
  if (query.platform) params.append('platform', query.platform);
  if (query.channel) params.append('channel', query.channel);
  if (query.status !== undefined) params.append('status', query.status.toString());
  if (query.page) params.append('page', query.page.toString());
  if (query.pageSize) params.append('pageSize', query.pageSize.toString());

  const response = await fetch(`${API_BASE}/list?${params}`, {
    headers: getAuthHeaders(),
  });
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '获取版本列表失败');
  }

  return data.data;
}

/**
 * 获取版本详情
 */
export async function getVersionById(id: number): Promise<AppVersion> {
  const response = await fetch(`${API_BASE}/${id}`, {
    headers: getAuthHeaders(),
  });
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '获取版本详情失败');
  }

  return data.data;
}

/**
 * 创建新版本
 */
export async function createVersion(formData: FormData): Promise<AppVersion> {
  const response = await fetch(`${API_BASE}`, {
    method: 'POST',
    headers: getAuthHeaders(false),
    body: formData,
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '创建版本失败');
  }

  return data.data;
}

/**
 * 更新版本信息
 */
export async function updateVersion(id: number, formData: FormData): Promise<AppVersion> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(false),
    body: formData,
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '更新版本失败');
  }

  return data.data;
}

/**
 * 删除版本
 */
export async function deleteVersion(id: number): Promise<void> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '删除版本失败');
  }
}

/**
 * 获取版本白名单
 */
export async function getVersionWhitelist(versionId: number): Promise<GrayDeviceWhitelist[]> {
  const response = await fetch(`${API_BASE}/${versionId}/whitelist`, {
    headers: getAuthHeaders(),
  });
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '获取白名单失败');
  }

  return data.data;
}

/**
 * 添加白名单设备
 */
export async function addWhitelistDevices(versionId: number, deviceIds: string[]): Promise<void> {
  const response = await fetch(`${API_BASE}/${versionId}/whitelist`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ deviceIds }),
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '添加白名单失败');
  }
}

/**
 * 删除白名单设备
 */
export async function removeWhitelistDevice(versionId: number, deviceId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/${versionId}/whitelist/${deviceId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '删除白名单设备失败');
  }
}

/**
 * 获取更新统计
 */
export async function getUpdateStats(startDate?: Date, endDate?: Date): Promise<{
  totalChecks: number;
  totalDownloads: number;
  totalInstalls: number;
  successRate: number;
}> {
  const params = new URLSearchParams();
  
  if (startDate) params.append('startDate', startDate.toISOString());
  if (endDate) params.append('endDate', endDate.toISOString());

  const response = await fetch(`${API_BASE}/stats/summary?${params}`, {
    headers: getAuthHeaders(),
  });
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '获取统计数据失败');
  }

  return data.data;
}

/**
 * 获取APK下载地址
 */
export function getApkDownloadUrl(filename: string): string {
  return `${API_BASE}/download/${filename}`;
}

